/**
 * Working Themes — FOMO view: themes whose baskets already started working vs SPY.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Flame, Zap, Moon, TrendingDown, Loader2, RefreshCw, Network, History, Radar } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { API } from "./stats/statsApi";
import { FlowTimeline } from "./ThemeFlows";

const pctS = (v, dp = 1) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;
const alphaColor = (v) =>
  v == null ? "text-muted-foreground" : v > 0 ? "text-emerald-400" : "text-red-400";

const CLS = {
  working: { label: "Working", icon: Flame, chip: "border-emerald-500/60 text-emerald-300 bg-emerald-500/10",
             help: "Basket beat SPY by ≥2% over 63d and holding over 21d. Forward: only +0.4%/3M (t 0.3) — confirmation, not a forward signal" },
  ignited: { label: "Just ignited", icon: Zap, chip: "border-amber-500/60 text-amber-300 bg-amber-500/10",
             help: "Just turned: ≥1.5% alpha over 21d without the 63d record yet" },
  quiet: { label: "Quiet", icon: Moon, chip: "border-sky-500/50 text-sky-300 bg-sky-500/10",
           help: "Not yet outperforming — where the forward edge lives (+2.7%/3M, t 1.8)" },
  fading: { label: "Fading", icon: TrendingDown, chip: "border-red-500/50 text-red-300 bg-red-500/10",
            help: "63d winner rolling over (negative 21d alpha)" },
};
const AGE = { young: "< 6m", established: "6–18m", veteran: "18m+" };

const WorkingThemeCard = ({ t, token, flowData }) => {
  const c = CLS[t.cls];
  const Icon = c.icon;
  const [adj, setAdj] = useState(null);
  const [adjLoading, setAdjLoading] = useState(false);
  const [showFlows, setShowFlows] = useState(false);
  const [radar, setRadar] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);

  const loadRadar = async () => {
    if (radar) { setRadar(null); return; }
    setRadarLoading(true);
    try {
      const res = await fetch(`${API}/themes/${t.theme_id}/radar`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setRadar(await res.json());
      else toast.error("Radar unavailable — pathways may need a rebuild");
    } finally {
      setRadarLoading(false);
    }
  };

  const loadAdj = async () => {
    if (adj) { setAdj(null); return; }
    setAdjLoading(true);
    try {
      const res = await fetch(`${API}/themes/${t.theme_id}/adjacency`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAdj(await res.json());
      else toast.error("No adjacency data for this theme");
    } finally {
      setAdjLoading(false);
    }
  };

  return (
    <Card data-testid={`working-theme-${t.theme_id}`} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span title={c.help} className={`px-2 py-0.5 rounded-full text-[11px] font-mono border inline-flex items-center gap-1 ${c.chip}`}>
                <Icon className="w-3 h-3" /> {c.label}
              </span>
              <h3 className="font-semibold text-sm">
                {t.name || `Unnamed (${t.symbols.slice(0, 3).join(", ")}…)`}
              </h3>
              <span className="text-[10px] font-mono text-muted-foreground">
                {t.status} · born {t.born || "?"} · {t.runs_seen} run{t.runs_seen > 1 ? "s" : ""} · age {AGE[t.age_label] || "?"}
              </span>
              {t.late_stage && (
                <span data-testid={`working-late-${t.theme_id}`}
                      title="Graph connectivity accelerating vs last run — crowding marker (rising-connectivity themes lag ~6%/3M)"
                      className="px-2 py-0.5 rounded-full text-[10px] font-mono border border-red-500/50 text-red-300 bg-red-500/10">
                  late-stage
                </span>
              )}
            </div>
            {t.thesis && (
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl line-clamp-2">{t.thesis}</p>
            )}
          </div>
          <div className="flex items-center gap-4 font-mono text-xs shrink-0">
            <div className="text-right">
              <div className="text-[10px] text-muted-foreground">α 21d</div>
              <div className={alphaColor(t.alpha_21d)}>{pctS(t.alpha_21d)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-muted-foreground">α 63d</div>
              <div className={`${alphaColor(t.alpha_63d)} font-semibold`}>{pctS(t.alpha_63d)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-muted-foreground">α since birth</div>
              <div className={alphaColor(t.alpha_since_birth)}>{pctS(t.alpha_since_birth, 0)}</div>
            </div>
            <div className="text-right w-20">
              <div className="text-[10px] text-muted-foreground">breadth 63d</div>
              <div className="h-1.5 bg-secondary rounded mt-1.5">
                <div className="h-1.5 rounded bg-emerald-500/70"
                     style={{ width: `${(t.breadth_63d * 100).toFixed(0)}%` }} />
              </div>
              <div className="text-[10px] mt-0.5">{(t.breadth_63d * 100).toFixed(0)}%</div>
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap mt-3 items-center" data-testid={`working-members-${t.theme_id}`}>
          {t.members.map((m) => (
            <span key={m.symbol}
                  title={`63d ${pctS(m.ret_63d)} · 21d ${pctS(m.ret_21d)} · α63 ${pctS(m.alpha_63d)}`}
                  className={`px-2 py-0.5 rounded border text-[11px] font-mono ${
                    m.role === "leader"
                      ? "border-emerald-500/40 text-emerald-300"
                      : "border-border text-muted-foreground"}`}>
              {m.symbol} <span className={alphaColor(m.alpha_63d)}>{pctS(m.alpha_63d, 0)}</span>
              {m.role === "laggard" && <span className="text-amber-400/80 ml-1">lag</span>}
            </span>
          ))}
          <button onClick={loadAdj} data-testid={`working-adjacency-btn-${t.theme_id}`}
                  className="px-2 py-0.5 rounded border border-violet-500/40 text-violet-300 text-[11px] font-mono inline-flex items-center gap-1 hover:bg-violet-500/10 transition-colors">
            {adjLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Network className="w-3 h-3" />}
            adjacent
          </button>
          <button onClick={loadRadar} data-testid={`working-radar-btn-${t.theme_id}`}
                  className="px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 text-[11px] font-mono inline-flex items-center gap-1 hover:bg-amber-500/10 transition-colors">
            {radarLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
            radar
          </button>
          {flowData?.flows?.length > 1 && (
            <button onClick={() => setShowFlows((v) => !v)}
                    data-testid={`working-flows-btn-${t.theme_id}`}
                    className="px-2 py-0.5 rounded border border-sky-500/40 text-sky-300 text-[11px] font-mono inline-flex items-center gap-1 hover:bg-sky-500/10 transition-colors">
              <History className="w-3 h-3" /> flows
            </button>
          )}
        </div>
        {showFlows && flowData && (
          <div className="pt-1 border-t border-border/50 mt-2" data-testid={`working-flows-${t.theme_id}`}>
            <FlowTimeline flows={flowData.flows} testid={`working-flow-timeline-${t.theme_id}`} />
          </div>
        )}
        {radar && (
          <div className="mt-2 pt-2 border-t border-border/50" data-testid={`working-radar-${t.theme_id}`}>
            <p className="text-[10px] font-mono text-muted-foreground mb-1.5">
              On the radar — names specialists holding this theme are likely assembling next
              (94.5% received a new specialist buy within 6M). FLOW FORECAST, not a return
              signal (candidates averaged −4.3%/3M vs SPY historically).
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {radar.candidates.map((cd) => (
                <span key={cd.symbol}
                      title={`${cd.name || ""} · ${cd.industry || ""} · via ${cd.via.join(", ")} · ${cd.n_pathway_funds} pathway funds`}
                      className="px-2 py-0.5 rounded border border-amber-500/40 text-amber-200 text-[11px] font-mono">
                  {cd.symbol} <span className="opacity-70">via {cd.via[0]}</span>
                  {cd.early_specialist_buys > 0 && (
                    <span className="ml-1 text-emerald-400/80">1st movers in</span>
                  )}
                </span>
              ))}
              {radar.candidates.length === 0 && (
                <span className="text-[11px] text-muted-foreground">No pathway candidates found.</span>
              )}
            </div>
          </div>
        )}
        {adj && (
          <div className="mt-2 pt-2 border-t border-border/50" data-testid={`working-adjacency-${t.theme_id}`}>
            <p className="text-[10px] font-mono text-muted-foreground mb-1.5">
              DNA-similar neighbors — on-deck watchlist (75% get a specialist buy within 6M; no standalone price edge pre-flow)
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {adj.candidates.map((cd) => (
                <span key={cd.symbol} title={`${cd.name || ""} · ${cd.industry || ""}`}
                      className={`px-2 py-0.5 rounded border text-[11px] font-mono ${
                        cd.in_13f_flow ? "border-border text-muted-foreground"
                                       : "border-violet-500/40 text-violet-300"}`}>
                  {cd.symbol} <span className="opacity-70">{(cd.similarity * 100).toFixed(0)}%</span>
                  {!cd.in_13f_flow && <span className="ml-1 text-violet-400/80">pre-13F</span>}
                </span>
              ))}
              {adj.candidates.length === 0 && (
                <span className="text-[11px] text-muted-foreground">No close neighbors found.</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const WorkingThemesView = ({ flowsById = {} }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clsF, setClsF] = useState("all");
  const [ageF, setAgeF] = useState("all");

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/themes/momentum${refresh ? "?refresh=true" : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { setError(`HTTP ${res.status}`); return; }
      setData(await res.json());
      setError(null);
      if (refresh) toast.success("Working themes recomputed");
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (error) return <p className="text-sm text-red-400" data-testid="working-error">{error}</p>;
  if (!data) return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground p-8">
      <Loader2 className="w-4 h-4 animate-spin" /> Computing basket momentum vs SPY…
    </div>
  );

  const themes = (data.themes || [])
    .filter((t) => (clsF === "all" || t.cls === clsF)
      && (ageF === "all" || t.age_label === ageF));

  return (
    <div className="space-y-4" data-testid="working-themes-view">
      <div className="flex items-center gap-2 flex-wrap">
        {[["all", `All (${data.n_themes})`],
          ...Object.entries(CLS).map(([k, v]) => [k, `${v.label} (${data.counts?.[k] ?? 0})`])]
          .map(([k, label]) => (
            <button key={k} onClick={() => setClsF(k)}
                    data-testid={`working-filter-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                      clsF === k ? (CLS[k]?.chip || "border-foreground/60 text-foreground bg-secondary")
                                 : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        <span className="w-px h-4 bg-border mx-1" />
        {[["all", "Any age"], ["young", "< 6m"], ["established", "6–18m"], ["veteran", "18m+"]]
          .map(([k, label]) => (
            <button key={k} onClick={() => setAgeF(k)}
                    data-testid={`working-age-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                      ageF === k ? "border-violet-500/60 text-violet-300 bg-violet-500/10"
                                 : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        <div className="flex-1" />
        <span className="text-[11px] font-mono text-muted-foreground">
          as of {data.as_of} · SPY 63d {pctS(data.spy_63d)}
        </span>
        <Button size="sm" variant="outline" onClick={() => load(true)} disabled={loading}
                data-testid="working-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                   : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      <div className="space-y-2">
        {themes.map((t) => <WorkingThemeCard key={t.theme_id} t={t} token={token}
                                             flowData={flowsById[t.theme_id]} />)}
        {themes.length === 0 && (
          <p className="text-sm text-muted-foreground p-6 text-center">No themes match the filters.</p>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground font-mono max-w-4xl" data-testid="working-evidence">
        Working = basket beat SPY by ≥2% over 63d and holding over 21d · Ignited = just turned
        (+1.5% α over 21d) · Fading = 63d winner rolling over. Benchmark: SPY, equal-weight.
        Validation (244 point-in-time formations, Jul 2023 → Jun 2026, fwd 3M vs SPY): quiet
        themes +2.7%/3M (t 1.8) vs working +0.4% (t 0.3) — the forward EDGE stays with early
        themes; this view is confirmation/showcase, not a forward-return screen. Within working
        themes: LEADERS +3.1%/3M vs laggards −3.7%/3M (t −2.0) — laggard "catch-up" buying
        underperforms; momentum lives in the leaders.
      </p>
    </div>
  );
};
